export type LogLevel = 'INFO' | 'WARN' | 'ERROR' | 'DEBUG'

export type LogEventType =
  | 'alarm'
  | 'configuration_change'
  | 'user_action'
  | 'badge_event'
  | 'video_extraction'
  | 'authentication'
  | 'device_status'
  | 'system'

export interface ExceptionInfo {
  Depth?: number
  ClassName?: string
  Message?: string
  Source?: string
  StackTraceString?: string
  RemoteStackTraceString?: string | null
  RemoteStackIndex?: number
  HResult?: number
  HelpURL?: string | null
}

export interface LogEntryMetadata {
  requestId?: string
  traceId?: string
  userId?: string
  clientIp?: string
  payload?: Record<string, unknown>
}

export interface LogEntry {
  id: string
  timestamp: string
  type: LogEventType
  level: LogLevel
  subsystem: string
  user: string | null
  description: string
  metadata: LogEntryMetadata
}

export interface LogPagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface LogResponse {
  data: LogEntry[]
  pagination: LogPagination
}

export type DatePreset = 'last1h' | 'last24h' | 'last7d' | 'last30d' | 'custom'

export type SortOrder = 'newest' | 'oldest'

export type SortField =
  | 'timestamp'
  | 'level'
  | 'source'
  | 'machine'
  | 'connection'
  | 'state'
  | 'user'
  | 'message'

export interface LogFilters {
  search: string
  level: LogLevel | 'ALL'
  // Selected services (sources), added as chips from the toolbar's source
  // picker. Logs from ANY of the selected services are shown; empty means all.
  subsystems: string[]
  datePreset: DatePreset
  dateFrom: string | null
  dateTo: string | null
  // Selected event types, added as chips from the toolbar's event picker.
  // Logs matching ANY of the selected types are shown; empty means all.
  eventTypes: LogEventType[]
  // Selected usernames, added as chips from the toolbar's user picker. Logs
  // matching ANY of the selected users are shown.
  users: string[]
  // Selected states (payload CommandType), added as chips from the toolbar's
  // state picker. Logs matching ANY of the selected states are shown; empty
  // means all.
  states: string[]
  // Exact correlation filters: when set, show only logs sharing this exact
  // Trace ID / Request ID (across every service). Driven by the "Correlate"
  // buttons in the detail panel. Only one is active at a time.
  traceId: string
  requestId: string
  sortField: SortField
  sortOrder: SortOrder
  page: number
  limit: number
}

export const DEFAULT_FILTERS: LogFilters = {
  search: '',
  level: 'ALL',
  subsystems: [],
  datePreset: 'last30d',
  dateFrom: null,
  dateTo: null,
  eventTypes: [],
  users: [],
  states: [],
  traceId: '',
  requestId: '',
  sortField: 'timestamp',
  sortOrder: 'newest',
  page: 1,
  limit: 25,
}

export const LOG_LEVELS: LogLevel[] = ['INFO', 'WARN', 'ERROR', 'DEBUG']

export const EVENT_TYPES: { value: LogEventType; label: string }[] = [
  { value: 'alarm', label: 'Alarm' },
  { value: 'configuration_change', label: 'Configuration Change' },
  { value: 'user_action', label: 'User Action' },
  { value: 'badge_event', label: 'Badge Event' },
  { value: 'video_extraction', label: 'Video Extraction' },
  { value: 'authentication', label: 'Authentication' },
  { value: 'device_status', label: 'Device Status' },
  { value: 'system', label: 'System' },
]

export const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'last1h', label: 'Last 1 Hour' },
  { value: 'last24h', label: 'Last 24 Hours' },
  { value: 'last7d', label: 'Last 7 Days' },
  { value: 'last30d', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Range' },
]
